import { type FunctionComponent, useCallback } from "react";

import { BaseLinkContextProvider } from "shared/components/common/base-link/context";
import { useCurrentPageType, useTrackImpressionEventByIntersection } from "shared/hooks";
import type { PageType } from "shared/lib/page-type";
import { getAbsoluteUrl, notEmpty } from "shared/lib/utils";

import { EntityLink } from "scmp-app/components/entity-link";
import {
  ArticleOneRow,
  BottomDivider,
  BottomItem,
  BottomRow,
  FullDivider,
  HeaderBlock,
  HeroImageLink,
  PrimaryText,
  SecondaryText,
  StyledPrimaryItem,
  StyledSecondaryItem,
  StyedContentCoverImage as StyledSectionStoryCoverImage,
} from "scmp-app/components/home-v2/section-story-block/styles";
import { sendGA4Tracking } from "scmp-app/components/tracking/google-analytics-4/apis";
import type {
  ArticleRecirculationWidgetName,
  RecirculationWidgetImpressionEvent,
} from "scmp-app/components/tracking/google-analytics-4/types";
import type { WidgetVersion } from "scmp-app/pages/home/ab-widget-fetch";
import type { batchQueryAbWidgetQuery$data } from "scmp-app/queries/__generated__/batchQueryAbWidgetQuery.graphql";
import type { entityLink$key } from "scmp-app/queries/__generated__/entityLink.graphql";

import { Container, Header, Title } from "./styles";

type Props = {
  className?: string;
  edges: WidgetEdges;
  fallbackTitle: string;
  moduleValue: string;
  recommendationId?: string;
  topicName?: string;
  topicReference?: entityLink$key | null;
  widgetName: ArticleRecirculationWidgetName;
  widgetVersion?: WidgetVersion;
  withBlockSection?: boolean;
};

type WidgetEdges =
  | batchQueryAbWidgetQuery$data["widget2"]["edges"]
  | batchQueryAbWidgetQuery$data["widget3"]["edges"]
  | batchQueryAbWidgetQuery$data["widget4"]["edges"];

export const AiSecondaryWidget: FunctionComponent<Props> = ({
  className,
  edges,
  fallbackTitle,
  moduleValue,
  recommendationId,
  topicName,
  topicReference,
  widgetName,
  widgetVersion,
  withBlockSection = false,
}) => {
  const nodes = edges
    .map(edge => edge.node)
    .filter(notEmpty)
    .filter(node => notEmpty(node.entityId))
    .slice(0, 4);

  const listOfArticles = nodes
    .map(node => node.entityId ?? "")
    .filter(Boolean)
    .join(",");

  const currentPageType = useCurrentPageType() as
    | PageType.Homepage
    | PageType.Section
    | PageType.SubSection;

  const impressionGA4Event = useCallback<() => RecirculationWidgetImpressionEvent>(
    () => ({
      action: "imp",
      category: "recirculation",
      customized_parameters: {
        ...(withBlockSection ? { block_section: topicName ?? "" } : {}),
        list_of_articles: listOfArticles,
        page_type: currentPageType,
        recommendation_id: recommendationId,
        result: "valid",
        widget_name: widgetName,
        ...(widgetVersion && { widget_version: widgetVersion }),
      },
      subcategory: "widget",
    }),
    [
      currentPageType,
      listOfArticles,
      recommendationId,
      topicName,
      widgetName,
      widgetVersion,
      withBlockSection,
    ],
  );

  const { captureTrackImpressionEventTargetElement } =
    useTrackImpressionEventByIntersection<RecirculationWidgetImpressionEvent>({
      ga4TrackingHandler: sendGA4Tracking,
      getGa4Event: impressionGA4Event,
      options: { isSendGA4Tracking: true, shouldSendOnce: true },
    });

  if (nodes.length === 0) return null;

  const [primary, secondary, third, fourth] = nodes;

  const handleClick = (entityId?: string, urlAlias?: string) => {
    sendGA4Tracking({
      action: "click",
      category: "recirculation",
      customized_parameters: {
        article_id: entityId,
        ...(withBlockSection ? { block_section: topicName ?? "" } : {}),
        destination_url: getAbsoluteUrl(urlAlias),
        list_of_articles: listOfArticles,
        page_type: currentPageType,
        recommendation_id: recommendationId ?? "",
        result: "valid",
        widget_name: widgetName,
        ...(widgetVersion && { widget_version: widgetVersion }),
      },
      subcategory: "widget",
    });
  };

  const moduleSuffix = widgetVersion === "cold_start" ? "_coldstart" : "";

  return (
    <BaseLinkContextProvider customQueryParameters={{ module: `${moduleValue}${moduleSuffix}` }}>
      <Container className={className} ref={captureTrackImpressionEventTargetElement}>
        <HeaderBlock>
          <Header>
            <Title>
              {topicReference ? (
                <EntityLink reference={topicReference}>{topicName ?? fallbackTitle}</EntityLink>
              ) : (
                (topicName ?? fallbackTitle)
              )}
            </Title>
          </Header>
        </HeaderBlock>

        {primary && (
          <ArticleOneRow>
            <PrimaryText>
              <StyledPrimaryItem
                isReadingTimeEnabled
                onClick={handleClick}
                reference={primary}
                withComment
                withSection
                withSummary
              />
            </PrimaryText>
            <HeroImageLink
              onClick={() => handleClick(primary?.entityId, primary?.urlAlias)}
              reference={primary}
            >
              <StyledSectionStoryCoverImage
                reference={primary}
                responsiveVariants={{
                  desktopUp: "size1200x800",
                  mobileUp: "size540x360",
                  tabletUp: "size1200x800",
                }}
              />
            </HeroImageLink>
            {secondary && (
              <SecondaryText>
                <StyledSecondaryItem
                  isReadingTimeEnabled
                  onClick={handleClick}
                  reference={secondary}
                  withComment
                  withSection
                />
              </SecondaryText>
            )}
          </ArticleOneRow>
        )}

        {(third || fourth) && (
          <>
            <FullDivider />
            <BottomRow>
              {third && (
                <BottomItem>
                  <StyledSecondaryItem
                    isReadingTimeEnabled
                    onClick={handleClick}
                    reference={third}
                    withComment
                    withSection
                  />
                </BottomItem>
              )}
              {third && fourth && <BottomDivider />}
              {fourth && (
                <BottomItem>
                  <StyledSecondaryItem
                    isReadingTimeEnabled
                    onClick={handleClick}
                    reference={fourth}
                    withComment
                    withSection
                  />
                </BottomItem>
              )}
            </BottomRow>
          </>
        )}
      </Container>
    </BaseLinkContextProvider>
  );
};

AiSecondaryWidget.displayName = "AiSecondaryWidget";
